import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Gift } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useLang, LangToggle } from "@/lib/i18n";

export default function AuthPage() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const { t } = useLang();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const url = mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login" ? { email: form.email, password: form.password } : form;
      const res = await api.post(url, payload);
      localStorage.setItem("session_token", res.data.session_token);
      setUser(res.data.user);
      toast.success(mode === "login" ? t("welcome_toast") : t("account_created"));
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || t("auth_error"));
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-background">
      <div className="w-full max-w-md fade-up">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-600 to-fuchsia-600 flex items-center justify-center shadow-[0_2px_10px_rgba(99,102,241,0.4)]">
            <Gift size={22} className="text-white" />
          </div>
          <span className="font-heading font-bold text-2xl">hediyeniiste</span>
        </Link>
        <div className="bg-card rounded-2xl border border-border/50 shadow-[0_8px_24px_rgba(0,0,0,0.04)] p-8">
          <div className="flex items-start justify-between">
            <h1 className="font-heading text-2xl font-bold mb-1">
              {mode === "login" ? t("welcome_back") : t("create_account")}
            </h1>
            <LangToggle />
          </div>
          <p className="text-sm text-muted-foreground mb-6">
            {mode === "login" ? t("back_to_lists") : t("free_start")}
          </p>
          <Button variant="outline" className="w-full rounded-full h-11 mb-4" onClick={googleLogin} data-testid="google-login-btn">
            <svg width="18" height="18" viewBox="0 0 24 24" className="mr-2">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 0 0 1 12c0 1.77.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {t("google_continue")}
          </Button>
          <div className="flex items-center gap-3 my-4">
            <div className="flex-1 h-px bg-border" />
            <span className="text-xs text-muted-foreground">{t("or_email")}</span>
            <div className="flex-1 h-px bg-border" />
          </div>
          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label htmlFor="name">{t("name_label")}</Label>
                <Input id="name" data-testid="auth-name-input" required value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-xl h-11" placeholder="Kemal Yılmaz" />
              </div>
            )}
            <div>
              <Label htmlFor="email">{t("email_label")}</Label>
              <Input id="email" type="email" data-testid="auth-email-input" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 rounded-xl h-11" placeholder="ornek@mail.com" />
            </div>
            <div>
              <Label htmlFor="password">{t("password_label")}</Label>
              <Input id="password" type="password" data-testid="auth-password-input" required value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 rounded-xl h-11" placeholder="••••••••" />
              {mode === "login" && (
                <div className="text-right mt-1">
                  <Link to="/sifre-unuttum" className="text-xs text-primary hover:underline" data-testid="forgot-password-link">{t("forgot")}</Link>
                </div>
              )}
            </div>
            <Button type="submit" disabled={busy} data-testid="auth-submit-btn"
              className="w-full rounded-full h-11 font-semibold active:scale-95 transition-transform">
              {busy ? t("wait") : mode === "login" ? t("login") : t("register")}
            </Button>
          </form>
          <p className="text-sm text-muted-foreground text-center mt-6">
            {mode === "login" ? t("no_account") : t("have_account")}{" "}
            <button data-testid="auth-toggle-mode" className="text-primary font-semibold"
              onClick={() => setMode(mode === "login" ? "register" : "login")}>
              {mode === "login" ? t("register_link") : t("login_link")}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
